import React, { useState } from 'react';
import buttonLocked from '../../../images/button_locked.gif';
import buttonUnlocked from '../../../images/button_unlocked.gif';
import { getDisplayFileType } from '../../utils/liveUrlTrackingExportUtils';
import './LiveUrlDataTable.css';

const getRecordKey = (record, index) => {
  if (record._id != null) {
    return typeof record._id === 'object' && typeof record._id.toString === 'function'
      ? record._id.toString()
      : String(record._id);
  }
  if (record.id != null) return String(record.id);
  if (record.fullPath) return record.fullPath;
  return String(index);
};

const LiveUrlDataTable = ({
  records = [],
  loading = false,
  error = null,
  currentSort = { field: 'pushedAt', order: 'desc' },
  onSort,
  onRetry,
  emptyMessage = 'No tracking records found'
}) => {
  const [copyStatus, setCopyStatus] = useState({});
  const timeouts = React.useRef({});

  React.useEffect(() => {
    // Cleanup function to clear all timeouts
    return () => {
      Object.values(timeouts.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  const handleCopyUrl = async (url, recordId, urlType) => {
    try {
      await navigator.clipboard.writeText(url);
      
      const key = `${recordId}-${urlType}`;
      setCopyStatus(prev => ({ ...prev, [key]: 'copied' }));
      
      // Clear success message after 2 seconds
      const timeoutId = setTimeout(() => {
        setCopyStatus(prev => ({ ...prev, [key]: null }));
      }, 2000);
      
      timeouts.current[key] = timeoutId;
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const formatDate = (dateString, recordIndex) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return <span data-testid={`invalid-date-${recordIndex}`}>Invalid Date</span>;
      }
      
      return (
        <div>
          <div data-testid={`formatted-date-${recordIndex}`}>
            {date.toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'short', 
              day: 'numeric' 
            })}
          </div>
          <div data-testid={`formatted-time-${recordIndex}`}>
            {date.toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            })}
          </div>
        </div>
      );
    } catch (err) {
      return <span data-testid={`invalid-date-${recordIndex}`}>Invalid Date</span>;
    }
  };

  const getSortIndicator = (field) => {
    if (currentSort.field === field) {
      return currentSort.order === 'desc' ? '↓' : '↑';
    }
    return '';
  };

  const handleHeaderClick = (field) => {
    const newOrder = currentSort.field === field && currentSort.order === 'desc' ? 'asc' : 'desc';
    onSort && onSort(field, newOrder);
  };

  const handleKeyPress = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  if (loading) {
    return (
      <div className="live-url-data-table" data-testid="live-url-data-table">
        <div data-testid="table-loading-skeleton" className="loading-skeleton">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} data-testid={`skeleton-row-${i}`} className="skeleton-row">
              Loading...
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="live-url-data-table" data-testid="live-url-data-table">
        <div data-testid="error-state" className="error-state">
          <div>{error.message}</div>
          {onRetry && (
            <button data-testid="retry-button" onClick={onRetry}>
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!records || records.length === 0) {
    return (
      <div className="live-url-data-table" data-testid="live-url-data-table">
        <div data-testid="empty-state" className="empty-state">
          <div data-testid="empty-state-illustration">📭</div>
          <div>{emptyMessage}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="table-wrapper" style={{ overflowX: 'auto' }}>
      <table 
        className="live-url-data-table responsive-table" 
        data-testid="live-url-data-table"
        role="table"
        aria-label="Live URL Tracking Records"
      >
        <thead>
          <tr>
            <th
              role="columnheader"
              data-testid="sort-header-pushedAt"
              tabIndex="0"
              onClick={() => handleHeaderClick('pushedAt')}
              onKeyDown={(e) => handleKeyPress(e, () => handleHeaderClick('pushedAt'))}
              aria-sort={currentSort.field === 'pushedAt' ? currentSort.order + 'ending' : 'none'}
              style={{ cursor: 'pointer' }}
            >
              Timestamp {getSortIndicator('pushedAt')}
            </th>
            <th
              role="columnheader"
              data-testid="sort-header-rootFolder"
              tabIndex="0"
              onClick={() => handleHeaderClick('rootFolder')}
              onKeyDown={(e) => handleKeyPress(e, () => handleHeaderClick('rootFolder'))}
              style={{ cursor: 'pointer' }}
            >
              Root Folder {getSortIndicator('rootFolder')}
            </th>
            <th role="columnheader" className="mobile-stack">File Path</th>
            <th role="columnheader" className="mobile-stack">File Type</th>
            <th role="columnheader" className="mobile-stack">Item Type</th>
            <th role="columnheader">Pushed By</th>
            <th role="columnheader">Live URLs</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record, index) => {
            const recordKey = getRecordKey(record, index);

            return (
            <tr key={recordKey} data-testid={`data-row-${recordKey}`}>
              <td>{formatDate(record.pushedAt, index + 1)}</td>
              <td>{record.rootFolder}</td>
              <td>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <img
                    src={record.enable_sms_protection === 'Enabled' ? buttonLocked : buttonUnlocked}
                    alt={record.enable_sms_protection === 'Enabled' ? 'SMS locked' : 'SMS unlocked'}
                    width="16"
                    height="16"
                    data-testid={`file-path-sms-icon-${recordKey}`}
                  />
                  {record.fullPath}
                </span>
              </td>
              <td>{getDisplayFileType(record)}</td>
              <td>{record.itemType}</td>
              <td>{record.pushedBy}</td>
              <td>
                <div className="url-list">
                  {record.liveUrls?.standard ? (
                    <div className="url-item">
                      <a
                        href={record.liveUrls.standard}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`live-url-standard-${recordKey}`}
                      >
                        Standard
                      </a>
                      <button
                        data-testid={`copy-url-standard-${recordKey}`}
                        aria-label="Copy Live URL"
                        title="Copy Live URL"
                        onClick={() => handleCopyUrl(record.liveUrls.standard, recordKey, 'standard')}
                        className="copy-btn"
                        tabIndex={0}
                      >
                        📋
                      </button>
                      {copyStatus[`${recordKey}-standard`] === 'copied' && (
                        <span className="copy-success">Copied!</span>
                      )}
                    </div>
                  ) : (
                    <div data-testid={`url-not-available-standard-${recordKey}`}>N/A</div>
                  )}

                  {record.liveUrls?.curriculum ? (
                    <div className="url-item">
                      <a
                        href={record.liveUrls.curriculum}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`live-url-curriculum-${recordKey}`}
                      >
                        Curriculum
                      </a>
                      <button
                        data-testid={`copy-url-curriculum-${recordKey}`}
                        aria-label="Copy Live URL"
                        title="Copy Live URL"
                        onClick={() => handleCopyUrl(record.liveUrls.curriculum, recordKey, 'curriculum')}
                        className="copy-btn"
                        tabIndex={0}
                      >
                        📋
                      </button>
                      {copyStatus[`${recordKey}-curriculum`] === 'copied' && (
                        <span className="copy-success">Copied!</span>
                      )}
                    </div>
                  ) : null}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default LiveUrlDataTable;